import { CoinsIcon, CurrencyDollarIcon, ForkKnifeIcon, GiftIcon, InvoiceIcon, ScalesIcon, TrendDownIcon, TrendUpIcon, UsersIcon, WalletIcon } from '@phosphor-icons/react'
import { currencyMask } from '@utils/masks/currencyMask'
import { RowBox } from '@components/shared/RowBox'

import type { CustomTooltipProps } from './types'
import type { ParsedChartAccount, ParsedChartBar } from '../../helpers/parseDataChart'

import * as S from './styles'

const categoryIconMap: Record<string, React.ReactNode> = {
  Faturamento: <CoinsIcon size={14} />,
  Consumo: <ForkKnifeIcon size={14} />,
  Pessoal: <UsersIcon size={14} />,
  Depreciação: <TrendDownIcon size={14} />,
  'Outras Despesas': <InvoiceIcon size={14} />,
  Bonificação: <GiftIcon size={14} />,
  'GP - Gross Profit': <TrendUpIcon size={14} />,
  'GM - Gross Margin': <ScalesIcon size={14} />
}

export const CustomTooltip = ({ data, active, payload, label }: CustomTooltipProps) => {
  if (!active || !payload || !payload.length) return null

  const originalItem = data?.find((item) => item.name === label)

  if (!originalItem) return null

  const dataKeyMap: Record<string, { name: string; color: string }> = {
    budget: { name: 'Budget', color: originalItem.budget.color },
    week1: { name: 'Semana 1', color: originalItem.week1.color },
    week2: { name: 'Semana 2', color: originalItem.week2.color },
    week3: { name: 'Semana 3', color: originalItem.week3.color },
    week4: { name: 'Semana 4', color: originalItem.week4.color },
    week5: { name: 'Semana 5', color: originalItem.week5.color }
  }

  const validPayloads = payload.filter(
    (item: any) => item.value !== null && item.value !== undefined && item.dataKey && dataKeyMap[item.dataKey as string]
  )

  if (!validPayloads || validPayloads.length === 0) return null

  const categoryIcon = label ? categoryIconMap[label] : null
  const currencyDollarCategories = ['Faturamento', 'Bonificação', 'GP - Gross Profit', 'GM - Gross Margin']
  const budgetIcon = label && currencyDollarCategories.includes(label)
    ? <CurrencyDollarIcon size={14} />
    : <WalletIcon size={14} />

  return (
    <S.CustomTooltipContainer borderColor={originalItem.week5.color}>
      <S.TooltipLabel>
        {categoryIcon && (
          <S.TooltipLabelIcon color={originalItem.week5.color}>
            {categoryIcon}
          </S.TooltipLabelIcon>
        )}
        {label}
      </S.TooltipLabel>

      {validPayloads.map((payloadItem: any, index: number) => {
        const dataKey = payloadItem.dataKey as string
        
        const itemInfo = dataKeyMap[dataKey]

        if (!itemInfo) return null

        /** RN02: valor real com sinal original preservado em parsedData — nunca usar payloadItem.value */
        const realValue = (originalItem[dataKey as keyof ParsedChartAccount] as ParsedChartBar | undefined)?.value ?? 0
        const isBudgetBar = dataKey === 'budget'
        const formatAsPercent = originalItem.formatAsPercent === true

        /**
         * GP e GM trazem desvio por semana; as demais contas trazem um desvio único consolidado.
         * O formato é independente disso: GP tem desvio semanal mas é monetário (RN04).
         */
        const weekDeviationAmount =
          (originalItem[`${dataKey}DeviationAmount` as keyof ParsedChartAccount] as number | undefined) ?? 0
        const deviationValue = originalItem.weeklyDeviation ? weekDeviationAmount : originalItem.deviationAmount ?? 0

        const formatValue = (value: number) =>
          formatAsPercent ? `${value.toFixed(2)}%` : currencyMask(value.toFixed(2))

        return (
          <S.TooltipItem key={`tooltip-item-${index}`}>
            <S.TooltipColorCircle color={itemInfo.color} />

            <S.TooltipValues>
              {!isBudgetBar && <S.TooltiplabelItem>{itemInfo.name}</S.TooltiplabelItem>}

              <RowBox gap={8} columns={2}>
                {isBudgetBar ? (
                  <>
                    <S.TooltiplabelItem>
                      <S.TooltipLabelIcon color={originalItem.week5.color}>
                        {budgetIcon}
                      </S.TooltipLabelIcon>
                      Budget:
                    </S.TooltiplabelItem>
                    <S.TooltipValue>{formatValue(realValue)}</S.TooltipValue>
                  </>
                ) : (
                  <>
                    <S.TooltiplabelItem>Prévia Atual:</S.TooltiplabelItem>
                    <S.TooltipValue>{formatValue(realValue)}</S.TooltipValue>
                    <S.TooltiplabelItem>Desvio:</S.TooltiplabelItem>
                    <S.TooltipValue>{formatValue(deviationValue)}</S.TooltipValue>
                  </>
                )}
              </RowBox>
            </S.TooltipValues>
          </S.TooltipItem>
        )
      })}
    </S.CustomTooltipContainer>
  )
}
